import React, { useCallback, useEffect, useRef } from 'react';
import { ElementContent } from 'hast';
import { toHtml } from 'hast-util-to-html';
import { HastAnchorElement, HastHTMLElement } from '../../types/hastElement';

type FootnoteItemProps = {
  id: string;
  index: number;
  liElement: HastHTMLElement<'li'>;
};

// helpers
const SCROLL_MARGIN = 100; // in pixels, header보다 조금 더 높은 값으로 여유롭게 잡음

function checkFootnoteBackButton(element: ElementContent) {
  return element.type === 'element' && (element as HastHTMLElement).properties.className?.includes('footnote-back');
}

function FootnoteItem({ id, index, liElement }: FootnoteItemProps): JSX.Element {
  const spanRef = useRef<HTMLSpanElement>(null);
  const backButton = liElement.children.find((el) => checkFootnoteBackButton(el)) as HastAnchorElement;
  const href = backButton.properties.href ?? '';

  // hast -> html 변환 (footnote의 children으로 입력된 html element들을 복구)
  useEffect(() => {
    if (spanRef.current) {
      spanRef.current.innerHTML = ''; // initalize

      liElement.children.forEach((childElement) => {
        // footnote 맨 뒤에 자동으로 붙는 '돌아가기' 버튼은 제거
        if (checkFootnoteBackButton(childElement)) {
          return;
        }

        // 그 외의 모든 html element는 정상적으로 출력
        const targetElement = { ...childElement };
        // - anchor element의 경우 target="_blank" attribute 부여
        if (targetElement.type === 'element' && targetElement.tagName === 'a' && targetElement.properties) {
          targetElement.properties['target'] = '_blank';
        }
        const html = toHtml(targetElement);
        spanRef.current!.innerHTML += html;
      });
    }
  }, [liElement]);

  // header가 footnote ref를 가리는 문제가 있어서 상단부에 조금 여유를 두고 스크롤하도록 처리
  const scrollWithMargin = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();

      const targetElement = document.querySelector(href); // href가 이미 "#fnref1"와 같이 DOM href의 형태를 띄고 있음
      if (targetElement) {
        const { top } = targetElement.getBoundingClientRect();
        window.history.pushState({}, '', href); // preventDefault를 사용했기 때문에 history를 직접 관리
        window.scrollTo(0, window.scrollY + top - SCROLL_MARGIN);
      }
    },
    [href]
  );

  return (
    <li id={id} role="doc-endnote" className={'footnote_item'}>
      <a className={'index'} href={href} onClick={scrollWithMargin}>
        [{index + 1}]
      </a>
      <span ref={spanRef}></span>
    </li>
  );
}

export default FootnoteItem;
